/**
 * Daily check-in submit helper.
 *
 * The check-in fans out to two backend endpoints: /pain-log and /daily-tracking.
 * We send them sequentially — the tracking endpoint triggers period-detection
 * that depends on pain-log notes already being persisted. Failure of either
 * bubbles up; the page surfaces a single error and leaves state untouched so
 * the user can retry from the last step.
 */

import { api } from '../../api'

export interface PainLogPayload {
  pain_score: number
  pain_character: string[]
  pain_locations: string[]
  notes: string
  log_date: string
}

export interface DailyTrackingPayload {
  sleep_hours: number
  energy_level: number | null
  cycle_phase: string | null
  symptoms: string[]
  stress_level: number | null
  food_tags: string[]
  alcohol: string | null
  cervical_mucus: string | null
  // Reported on bleeding days as two separate counts. Drive the consult-note
  // "Heaviest day" line. flow_products_count is kept as their sum so existing
  // readers (and historical rows) keep working.
  pads_count: number | null
  tampons_count: number | null
  flow_products_count: number | null
  log_date: string
}

/**
 * "Unsure" is presented as an option to the user but must not poison analytics.
 * The server treats null and missing identically, so we normalize both here.
 */
export function toNullable<T extends string | null | undefined>(v: T): string | null {
  if (!v || v === 'unsure') return null
  return v
}

export function todayLocalISO(): string {
  return new Date().toLocaleDateString('en-CA')
}

export async function submitCheckin(
  pain: PainLogPayload,
  tracking: DailyTrackingPayload,
): Promise<void> {
  await api.post('/api/patient/pain-log', pain as unknown as Record<string, unknown>)
  await api.post('/api/patient/daily-tracking', tracking as unknown as Record<string, unknown>)
}
